import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch, FiCalendar, FiClock, FiDollarSign, FiUser,
  FiEdit2, FiX, FiCheck, FiAlertCircle, FiPlus, FiShield, FiCheckCircle, FiEye, FiImage, FiChevronDown, FiChevronUp, FiFileText
} from 'react-icons/fi';
import { ManagerLayout } from '../../components/layout';
import { Card, Button, Loading, AlertModal, ConfirmDialog, Modal } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { getBookingsByCompany, updateBooking, cancelBooking, createBooking, getBookingById, deleteBookingImage } from '../../firebase/bookings';
import { getCarsByCompany } from '../../firebase/cars';
import { formatDateTime } from '../../data/carsData';
import { getUserById, updateUserProfile } from '../../firebase/users';
import { getCompanyById } from '../../firebase/companies';
import BookingImageUpload from '../../components/BookingImageUpload';
import BookingImageGallery from '../../components/BookingImageGallery';

const BookingList = () => {
  const { managerCompanyId, user, userProfile } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showOfflineBookingForm, setShowOfflineBookingForm] = useState(false);
  const [offlineBookingData, setOfflineBookingData] = useState({
    carId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    fromDate: '',
    fromTime: '10',
    toDate: '',
    toTime: '10',
    totalAmount: '',
    notes: ''
  });
  const [offlineBookingErrors, setOfflineBookingErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [customerDocuments, setCustomerDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [verifyingDoc, setVerifyingDoc] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '', type: 'warning' });
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [bookingToComplete, setBookingToComplete] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);

  useEffect(() => {
    if (managerCompanyId) {
      loadData();
    }
  }, [managerCompanyId]);

  const loadData = async () => {
    if (!managerCompanyId) return;
    
    setLoading(true);
    try {
      const [bookingsResult, carsResult] = await Promise.all([
        getBookingsByCompany(managerCompanyId),
        getCarsByCompany(managerCompanyId, { includeInactive: true })
      ]);
      
      setBookings(bookingsResult.bookings || []);
      setCars(carsResult.cars || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.bookingNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.customerDetails?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.customerDetails?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.carDetails?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Handle status update
  const handleStatusUpdate = (bookingId, newStatus) => {
    // If marking as completed, show modal for image upload
    if (newStatus === 'completed') {
      const booking = bookings.find(b => b.id === bookingId);
      setBookingToComplete(booking);
      setShowCompleteModal(true);
      return;
    }

    // For other status updates, use confirm dialog
    setConfirmDialog({
      isOpen: true,
      title: 'Change Booking Status',
      message: `Are you sure you want to change the booking status to ${newStatus}?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          const result = await updateBooking(bookingId, { status: newStatus });
          if (result.error) {
            setAlert({
              isOpen: true,
              type: 'error',
              title: 'Update Failed',
              message: result.error || 'Failed to update status. Please try again.'
            });
          } else {
            setAlert({
              isOpen: true,
              type: 'success',
              title: 'Status Updated',
              message: `Booking status has been changed to ${newStatus}.`
            });
            loadData();
          }
        } catch (error) {
          console.error('Error updating booking:', error);
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Update Failed',
            message: 'Failed to update booking status. Please try again.'
          });
        }
      }
    });
  };

  // Handle complete booking (with optional image upload)
  const handleCompleteBooking = async (skipImages = false) => {
    if (!bookingToComplete) return;

    try {
      const result = await updateBooking(bookingToComplete.id, { status: 'completed' });
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Update Failed',
          message: result.error || 'Failed to complete booking. Please try again.'
        });
      } else {
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Booking Completed',
          message: 'Booking has been marked as completed.'
        });
        setShowCompleteModal(false);
        setBookingToComplete(null);
        loadData();
      }
    } catch (error) {
      console.error('Error completing booking:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Update Failed',
        message: 'An error occurred. Please try again.'
      });
    }
  };

  const handleImageUploadComplete = async (bookingId) => {
    // Reload the specific booking to get updated images
    try {
      const result = await getBookingById(bookingId);
      if (result.booking) {
        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, ...result.booking } : b
        ));
        // Also update bookingToComplete if it's the same
        if (bookingToComplete && bookingToComplete.id === bookingId) {
          setBookingToComplete(result.booking);
        }
      }
    } catch (error) {
      console.error('Error reloading booking:', error);
    }
  };

  const handleImageDelete = async (bookingId, imageId, imageType, imagePath) => {
    try {
      const result = await deleteBookingImage(bookingId, imageId, imageType, imagePath);
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Delete Failed',
          message: result.error || 'Failed to delete image. Please try again.'
        });
        return;
      }
      
      // Reload booking
      await handleImageUploadComplete(bookingId);
    } catch (error) {
      console.error('Error deleting image:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete image. Please try again.'
      });
    }
  };

  // Handle view documents
  const handleViewDocuments = async (booking) => {
    if (!booking.userId) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'User Not Found',
        message: 'User ID not available for this booking.'
      });
      return;
    }

    setSelectedBooking(booking);
    setShowDocumentsModal(true);
    setLoadingDocuments(true);
    setCustomerDocuments(null);

    try {
      const result = await getUserById(booking.userId);
      if (result.user && result.user.documents) {
        setCustomerDocuments(result.user.documents);
      } else {
        setCustomerDocuments({});
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Load Failed',
        message: 'Failed to load documents. Please try again.'
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Handle verify document
  const handleVerifyDocument = async (documentType) => {
    if (!selectedBooking?.userId || !user) {
      return;
    }

    setVerifyingDoc(documentType);

    try {
      // Get company name for verification record
      // Use managerCompanyId (the company the manager/owner belongs to)
      const companyIdToFetch = managerCompanyId;
      let companyNameResolved = 'Unknown Company';
      
      if (companyIdToFetch) {
        try {
          const companyResult = await getCompanyById(companyIdToFetch);
          if (companyResult.company && companyResult.company.name) {
            companyNameResolved = companyResult.company.name;
          } else {
            console.warn('Company not found for ID:', companyIdToFetch);
          }
        } catch (error) {
          console.error('Error fetching company name:', error);
          companyNameResolved = 'Unknown Company';
        }
      } else {
        console.warn('No company ID available for verification');
      }
      
      const verificationEntry = {
        verified: true,
        verifiedBy: user.uid,
        verifiedByName: user.displayName || user.email || 'Manager',
        verifiedByRole: userProfile?.role || 'manager',
        verifiedByCompanyId: companyIdToFetch || null,
        verifiedByCompanyName: companyNameResolved,
        verifiedAt: new Date().toISOString(),
        bookingId: selectedBooking.id,
        bookingNumber: selectedBooking.bookingNumber
      };

      const verificationData = {
        [`documents.${documentType}.verified`]: true,
        [`documents.${documentType}.verifiedBy`]: user.uid,
        [`documents.${documentType}.verifiedByName`]: user.displayName || user.email || 'Manager',
        [`documents.${documentType}.verifiedByRole`]: userProfile?.role || 'manager',
        [`documents.${documentType}.verifiedByCompanyId`]: companyIdToFetch || null,
        [`documents.${documentType}.verifiedByCompanyName`]: companyNameResolved,
        [`documents.${documentType}.verifiedAt`]: new Date().toISOString(),
        [`documents.${documentType}.lastVerifiedBy`]: user.uid,
        [`documents.${documentType}.lastVerifiedAt`]: new Date().toISOString(),
        [`documents.${documentType}.verificationHistory`]: [
          ...(customerDocuments?.[documentType]?.verificationHistory || []),
          verificationEntry
        ]
      };

      const { error } = await updateUserProfile(selectedBooking.userId, verificationData);

      if (error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Verification Failed',
          message: `Failed to verify document: ${error}`
        });
      } else {
        // Reload documents
        const result = await getUserById(selectedBooking.userId);
        if (result.user && result.user.documents) {
          setCustomerDocuments(result.user.documents);
        }
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Document Verified',
          message: 'Document verified successfully!'
        });
      }
    } catch (error) {
      console.error('Error verifying document:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Verification Failed',
        message: 'Failed to verify document. Please try again.'
      });
    } finally {
      setVerifyingDoc(null);
    }
  };

  // Handle cancel booking
  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking);
    setCancelReason('');
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Reason Required',
        message: 'Please provide a reason for cancellation.'
      });
      return;
    }

    try {
      const result = await cancelBooking(bookingToCancel.id, cancelReason.trim(), 'manager');
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Cancellation Failed',
          message: result.error || 'Failed to cancel booking. Please try again.'
        });
      } else {
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Booking Cancelled',
          message: 'Booking has been cancelled successfully.'
        });
        setShowCancelDialog(false);
        setBookingToCancel(null);
        setCancelReason('');
        loadData();
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Cancellation Failed',
        message: 'Failed to cancel booking. Please try again.'
      });
    }
  };

  // Handle offline booking form
  const handleOfflineBookingChange = (e) => {
    const { name, value } = e.target;
    setOfflineBookingData(prev => ({
      ...prev,
      [name]: value
    }));
    if (offlineBookingErrors[name]) {
      setOfflineBookingErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateOfflineBooking = () => {
    const errors = {};
    
    if (!offlineBookingData.carId) errors.carId = 'Please select a car';
    if (!offlineBookingData.customerName?.trim()) errors.customerName = 'Customer name is required';
    if (!offlineBookingData.customerPhone?.trim()) errors.customerPhone = 'Customer phone is required';
    if (!offlineBookingData.fromDate) errors.fromDate = 'Start date is required';
    if (!offlineBookingData.fromTime) errors.fromTime = 'Start time is required';
    if (!offlineBookingData.toDate) errors.toDate = 'End date is required';
    if (!offlineBookingData.toTime) errors.toTime = 'End time is required';
    if (!offlineBookingData.totalAmount || Number(offlineBookingData.totalAmount) <= 0) {
      errors.totalAmount = 'Valid amount is required';
    }
    
    // Check date logic
    if (offlineBookingData.fromDate && offlineBookingData.toDate && offlineBookingData.fromTime && offlineBookingData.toTime) {
      const fromDateTime = `${offlineBookingData.fromDate}T${offlineBookingData.fromTime}:00`;
      const toDateTime = `${offlineBookingData.toDate}T${offlineBookingData.toTime}:00`;
      const from = new Date(fromDateTime);
      const to = new Date(toDateTime);
      if (to <= from) {
        errors.toDate = 'End date/time must be after start date/time';
      }
    }
    
    setOfflineBookingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateOfflineBooking = async (e) => {
    e.preventDefault();
    
    if (!validateOfflineBooking()) return;
    
    setSubmitting(true);
    
    try {
      const selectedCar = cars.find(c => c.id === offlineBookingData.carId);
      
      // Convert date and time to IST timezone string
      const fromDateTimeIST = `${offlineBookingData.fromDate}T${offlineBookingData.fromTime.padStart(2, '0')}:00:00+05:30`;
      const toDateTimeIST = `${offlineBookingData.toDate}T${offlineBookingData.toTime.padStart(2, '0')}:00:00+05:30`;
      
      const bookingData = {
        companyId: managerCompanyId,
        carId: offlineBookingData.carId,
        userId: null, // Offline booking - no user account
        fromDateTime: fromDateTimeIST,
        toDateTime: toDateTimeIST,
        totalAmount: Number(offlineBookingData.totalAmount),
        status: 'confirmed',
        paymentStatus: 'paid', // Offline bookings are typically paid
        bookingType: 'offline',
        customerDetails: {
          name: offlineBookingData.customerName.trim(),
          email: offlineBookingData.customerEmail?.trim() || '',
          phone: offlineBookingData.customerPhone.trim()
        },
        carDetails: {
          name: selectedCar?.name || 'Unknown Car',
          image: selectedCar?.images?.[0] || ''
        },
        notes: offlineBookingData.notes?.trim() || '',
        createdBy: 'manager'
      };
      
      const result = await createBooking(bookingData);
      
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Booking Failed',
          message: result.error || 'Failed to create booking. Please try again.'
        });
      } else {
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Booking Created',
          message: 'Offline booking created successfully!'
        });
        setShowOfflineBookingForm(false);
        setOfflineBookingData({
          carId: '',
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          fromDate: '',
          fromTime: '10',
          toDate: '',
          toTime: '10',
          totalAmount: '',
          notes: ''
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating offline booking:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Booking Failed',
        message: 'Failed to create offline booking. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // formatDateTime is imported from carsData.js

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <ManagerLayout title="Bookings">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </ManagerLayout>
    );
  }

  return (
    <ManagerLayout title="Bookings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bookings</h2>
          <p className="text-gray-600">{bookings.length} total bookings</p>
        </div>
        <Button
          onClick={() => setShowOfflineBookingForm(true)}
          className="flex items-center gap-2"
        >
          <FiPlus size={18} />
          Create Offline Booking
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by booking number, customer, or car..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </Card>

      {/* Offline Booking Form Modal */}
      {showOfflineBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Create Offline Booking</h3>
                <button
                  onClick={() => {
                    setShowOfflineBookingForm(false);
                    setOfflineBookingErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <form onSubmit={handleCreateOfflineBooking} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Car *
                    </label>
                    <select
                      name="carId"
                      value={offlineBookingData.carId}
                      onChange={handleOfflineBookingChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        offlineBookingErrors.carId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a car</option>
                      {cars.filter(c => c.status === 'active').map(car => (
                        <option key={car.id} value={car.id}>{car.name}</option>
                      ))}
                    </select>
                    {offlineBookingErrors.carId && (
                      <p className="text-red-500 text-sm mt-1">{offlineBookingErrors.carId}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Amount (₹) *
                    </label>
                    <input
                      type="number"
                      name="totalAmount"
                      value={offlineBookingData.totalAmount}
                      onChange={handleOfflineBookingChange}
                      min="0"
                      step="0.01"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        offlineBookingErrors.totalAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {offlineBookingErrors.totalAmount && (
                      <p className="text-red-500 text-sm mt-1">{offlineBookingErrors.totalAmount}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={offlineBookingData.customerName}
                      onChange={handleOfflineBookingChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        offlineBookingErrors.customerName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {offlineBookingErrors.customerName && (
                      <p className="text-red-500 text-sm mt-1">{offlineBookingErrors.customerName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Phone *
                    </label>
                    <input
                      type="tel"
                      name="customerPhone"
                      value={offlineBookingData.customerPhone}
                      onChange={handleOfflineBookingChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        offlineBookingErrors.customerPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {offlineBookingErrors.customerPhone && (
                      <p className="text-red-500 text-sm mt-1">{offlineBookingErrors.customerPhone}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Email
                    </label>
                    <input
                      type="email"
                      name="customerEmail"
                      value={offlineBookingData.customerEmail}
                      onChange={handleOfflineBookingChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                {/* Start Date & Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Start Date & Time *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Date
                      </label>
                      <input
                        type="date"
                        name="fromDate"
                        value={offlineBookingData.fromDate}
                        onChange={handleOfflineBookingChange}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          offlineBookingErrors.fromDate ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                      {offlineBookingErrors.fromDate && (
                        <p className="text-red-500 text-xs mt-1">{offlineBookingErrors.fromDate}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Time (Hour)
                      </label>
                      <select
                        name="fromTime"
                        value={offlineBookingData.fromTime}
                        onChange={handleOfflineBookingChange}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          offlineBookingErrors.fromTime ? 'border-red-500' : 'border-gray-200'
                        }`}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i;
                          const period = hour >= 12 ? 'PM' : 'AM';
                          const hour12 = hour % 12 || 12;
                          return (
                            <option key={hour} value={hour}>
                              {hour12}:00 {period}
                            </option>
                          );
                        })}
                      </select>
                      {offlineBookingErrors.fromTime && (
                        <p className="text-red-500 text-xs mt-1">{offlineBookingErrors.fromTime}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* End Date & Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    End Date & Time *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Date
                      </label>
                      <input
                        type="date"
                        name="toDate"
                        value={offlineBookingData.toDate}
                        onChange={handleOfflineBookingChange}
                        min={offlineBookingData.fromDate || new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          offlineBookingErrors.toDate ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                      {offlineBookingErrors.toDate && (
                        <p className="text-red-500 text-xs mt-1">{offlineBookingErrors.toDate}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Time (Hour)
                      </label>
                      <select
                        name="toTime"
                        value={offlineBookingData.toTime}
                        onChange={handleOfflineBookingChange}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          offlineBookingErrors.toTime ? 'border-red-500' : 'border-gray-200'
                        }`}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i;
                          const period = hour >= 12 ? 'PM' : 'AM';
                          const hour12 = hour % 12 || 12;
                          return (
                            <option key={hour} value={hour}>
                              {hour12}:00 {period}
                            </option>
                          );
                        })}
                      </select>
                      {offlineBookingErrors.toTime && (
                        <p className="text-red-500 text-xs mt-1">{offlineBookingErrors.toTime}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={offlineBookingData.notes}
                    onChange={handleOfflineBookingChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Additional notes about this booking..."
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowOfflineBookingForm(false);
                      setOfflineBookingErrors({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-2"
                  >
                    {submitting ? <Loading size="sm" /> : <FiCheck size={18} />}
                    Create Booking
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Bookings List */}
      {filteredBookings.length > 0 ? (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <Card key={booking.id} className="p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {booking.carDetails?.name || 'Unknown Car'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Booking #{booking.bookingNumber || booking.id}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiUser size={16} />
                      <span>{booking.customerDetails?.name || booking.customerDetails?.email || 'Unknown Customer'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiCalendar size={16} />
                      <span>{formatDateTime(booking.fromDateTime)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiClock size={16} />
                      <span>{formatDateTime(booking.toDateTime)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiDollarSign size={16} />
                      <span className="font-semibold text-gray-900">₹{booking.totalAmount?.toLocaleString() || 0}</span>
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Customer Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Name:</span> {booking.customerDetails?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {booking.customerDetails?.email || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {booking.customerDetails?.phone || 'N/A'}
                      </div>
                      {booking.customerDetails?.alternatePhone && (
                        <div>
                          <span className="font-medium">Alternate Phone:</span> {booking.customerDetails.alternatePhone}
                        </div>
                      )}
                      {booking.customerDetails?.whatsappNumber && (
                        <div>
                          <span className="font-medium">WhatsApp:</span> {booking.customerDetails.whatsappNumber}
                        </div>
                      )}
                      {booking.customerDetails?.aadharNumber && (
                        <div>
                          <span className="font-medium">Aadhar:</span> {booking.customerDetails.aadharNumber.replace(/(\d{4})\s?(\d{4})\s?(\d{4})/, 'XXXX XXXX $3')}
                        </div>
                      )}
                      {booking.customerDetails?.address && (
                        <div className="md:col-span-2">
                          <span className="font-medium">Address:</span> {booking.customerDetails.address}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Signature */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <FiFileText size={16} />
                      Digital Signature
                    </h4>
                    {booking.signature?.image ? (
                      <>
                        <div className="relative inline-block">
                          <img
                            src={booking.signature.image}
                            alt="Customer Signature"
                            className="h-32 border-2 border-gray-300 rounded-lg bg-white p-2 shadow-sm"
                          />
                          <button
                            onClick={() => {
                              const modal = window.open('', '_blank', 'width=600,height=400');
                              modal.document.write(`
                                <html>
                                  <head><title>Customer Signature</title></head>
                                  <body style="margin:0;padding:20px;display:flex;justify-content:center;align-items:center;background:#f3f4f6;">
                                    <img src="${booking.signature.image}" style="max-width:100%;max-height:100%;border:1px solid #d1d5db;border-radius:8px;background:white;padding:10px;" />
                                  </body>
                                </html>
                              `);
                            }}
                            className="ml-2 text-xs text-primary-600 hover:text-primary-700 underline font-medium"
                          >
                          View Full Size
                          </button>
                        </div>
                        {booking.signature.signedAt && (
                          <p className="text-xs text-gray-500 mt-2">
                            Signed: {new Date(booking.signature.signedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No signature provided</p>
                    )}
                  </div>
                  
                  {booking.bookingType === 'offline' && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <FiAlertCircle size={14} />
                      <span>Offline Booking</span>
                    </div>
                  )}
                  
                  {booking.notes && (
                    <p className="text-sm text-gray-600 italic">Note: {booking.notes}</p>
                  )}

                  {/* Car Images Section */}
                  {(booking.images?.beforeRide?.length > 0 || booking.images?.afterRide?.length > 0 || (booking.status === 'confirmed' || booking.status === 'completed')) && (
                    <div className="pt-4 border-t border-gray-200 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          <FiImage size={16} />
                          Car Images
                        </h4>
                        <button
                          onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                          className="text-primary-600 hover:text-primary-700 p-1 rounded transition-colors"
                          title={expandedBooking === booking.id ? 'Hide Images' : 'View Images'}
                        >
                          {expandedBooking === booking.id ? (
                            <FiChevronUp size={18} />
                          ) : (
                            <FiChevronDown size={18} />
                          )}
                        </button>
                      </div>

                      {/* Show images only when expanded */}
                      {expandedBooking === booking.id && (
                        <>
                          {/* Before Ride Images (User uploaded) */}
                          {booking.images?.beforeRide && booking.images.beforeRide.length > 0 && (
                            <div className="mb-4">
                              <BookingImageGallery
                                images={booking.images.beforeRide}
                                imageType="before-ride"
                                currentUserId={user?.uid}
                                onDelete={null}
                                canDelete={false}
                              />
                            </div>
                          )}

                          {/* After Ride Images (Manager uploaded) */}
                          {booking.images?.afterRide && booking.images.afterRide.length > 0 && (
                            <div className="mb-4">
                              <BookingImageGallery
                                images={booking.images.afterRide}
                                imageType="after-ride"
                                currentUserId={user?.uid}
                                onDelete={(imageId, imageType, imagePath) => handleImageDelete(booking.id, imageId, imageType, imagePath)}
                                canDelete={true}
                              />
                            </div>
                          )}

                          {/* Upload Section (for confirmed or completed bookings) */}
                          {(booking.status === 'confirmed' || booking.status === 'completed') && (
                            <div className="space-y-6 mt-4">
                              <div className="p-4 bg-gray-50 rounded-lg">
                                <h5 className="text-sm font-medium text-gray-900 mb-3">After Ride Images</h5>
                                <p className="text-xs text-gray-600 mb-3">
                                  Upload images of the car after the ride to document any damages or conditions.
                                </p>
                                <BookingImageUpload
                                  bookingId={booking.id}
                                  imageType="after-ride"
                                  currentImages={booking.images?.afterRide || []}
                                  currentUserId={user?.uid}
                                  currentUserName={userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Manager'}
                                  onUploadComplete={() => handleImageUploadComplete(booking.id)}
                                  onDelete={(imageId, imageType, imagePath) => handleImageDelete(booking.id, imageId, imageType, imagePath)}
                                  maxImages={15}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col gap-2">
                  {booking.userId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDocuments(booking)}
                      className="flex items-center gap-2"
                    >
                      <FiEye size={14} />
                      View Documents
                    </Button>
                  )}
                  {booking.status === 'confirmed' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusUpdate(booking.id, 'completed')}
                        className="flex items-center gap-2"
                      >
                        <FiCheck size={14} />
                        Mark Completed
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelBooking(booking)}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700"
                      >
                        <FiX size={14} />
                        Cancel
                      </Button>
                    </>
                  )}
                  {booking.status === 'pending' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
                        className="flex items-center gap-2"
                      >
                        <FiCheck size={14} />
                        Confirm
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelBooking(booking)}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700"
                      >
                        <FiX size={14} />
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FiCalendar size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No bookings found' : 'No bookings yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Bookings will appear here once customers make reservations'
            }
          </p>
        </Card>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Customer Documents</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDocumentsModal(false);
                  setSelectedBooking(null);
                  setCustomerDocuments(null);
                }}
              >
                <FiX size={16} />
              </Button>
            </div>

            {loadingDocuments ? (
              <div className="flex items-center justify-center py-12">
                <Loading />
              </div>
            ) : !customerDocuments || Object.keys(customerDocuments).length === 0 ? (
              <div className="text-center py-12">
                <FiShield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No documents uploaded by this customer</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* License Front */}
                {customerDocuments['license-front']?.url && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">License Front</h3>
                      {customerDocuments['license-front'].verified ? (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          <FiCheckCircle size={12} />
                          Verified
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyDocument('license-front')}
                          disabled={verifyingDoc === 'license-front'}
                          className="text-sm"
                        >
                          {verifyingDoc === 'license-front' ? 'Verifying...' : 'Verify'}
                        </Button>
                      )}
                    </div>
                    <img
                      src={customerDocuments['license-front'].url}
                      alt="License Front"
                      className="w-full h-auto rounded-lg border border-gray-200"
                    />
                    {customerDocuments['license-front'].verified && customerDocuments['license-front'].verificationHistory && (
                      <div className="mt-2 text-xs text-gray-600">
                        Verified by {customerDocuments['license-front'].verifiedByName} on{' '}
                        {new Date(customerDocuments['license-front'].verifiedAt).toLocaleDateString('en-IN')}
                      </div>
                    )}
                  </div>
                )}

                {/* License Back */}
                {customerDocuments['license-back']?.url && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">License Back</h3>
                      {customerDocuments['license-back'].verified ? (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          <FiCheckCircle size={12} />
                          Verified
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyDocument('license-back')}
                          disabled={verifyingDoc === 'license-back'}
                          className="text-sm"
                        >
                          {verifyingDoc === 'license-back' ? 'Verifying...' : 'Verify'}
                        </Button>
                      )}
                    </div>
                    <img
                      src={customerDocuments['license-back'].url}
                      alt="License Back"
                      className="w-full h-auto rounded-lg border border-gray-200"
                    />
                    {customerDocuments['license-back'].verified && customerDocuments['license-back'].verificationHistory && (
                      <div className="mt-2 text-xs text-gray-600">
                        Verified by {customerDocuments['license-back'].verifiedByName} on{' '}
                        {new Date(customerDocuments['license-back'].verifiedAt).toLocaleDateString('en-IN')}
                      </div>
                    )}
                  </div>
                )}

                {/* Aadhar Front */}
                {customerDocuments['aadhar-front']?.url && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">Aadhar Front</h3>
                      {customerDocuments['aadhar-front'].verified ? (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          <FiCheckCircle size={12} />
                          Verified
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyDocument('aadhar-front')}
                          disabled={verifyingDoc === 'aadhar-front'}
                          className="text-sm"
                        >
                          {verifyingDoc === 'aadhar-front' ? 'Verifying...' : 'Verify'}
                        </Button>
                      )}
                    </div>
                    <img
                      src={customerDocuments['aadhar-front'].url}
                      alt="Aadhar Front"
                      className="w-full h-auto rounded-lg border border-gray-200"
                    />
                    {customerDocuments['aadhar-front'].verified && customerDocuments['aadhar-front'].verificationHistory && (
                      <div className="mt-2 text-xs text-gray-600">
                        Verified by {customerDocuments['aadhar-front'].verifiedByName} on{' '}
                        {new Date(customerDocuments['aadhar-front'].verifiedAt).toLocaleDateString('en-IN')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      {/* Complete Booking Modal with Image Upload */}
      {showCompleteModal && bookingToComplete && (
        <Modal
          isOpen={showCompleteModal}
          onClose={() => {
            setShowCompleteModal(false);
            setBookingToComplete(null);
          }}
          title="Complete Booking"
          size="large"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Before marking this booking as completed, you can upload images of the car after the ride to document any damages or conditions.
            </p>

            {/* Existing After Ride Images */}
            {bookingToComplete.images?.afterRide && bookingToComplete.images.afterRide.length > 0 && (
              <div>
                <BookingImageGallery
                  images={bookingToComplete.images.afterRide}
                  imageType="after-ride"
                  currentUserId={user?.uid}
                  onDelete={(imageId, imageType, imagePath) => handleImageDelete(bookingToComplete.id, imageId, imageType, imagePath)}
                  canDelete={true}
                />
              </div>
            )}

            {/* Image Upload Section */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <BookingImageUpload
                bookingId={bookingToComplete.id}
                imageType="after-ride"
                currentImages={bookingToComplete.images?.afterRide || []}
                currentUserId={user?.uid}
                currentUserName={userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Manager'}
                onUploadComplete={() => handleImageUploadComplete(bookingToComplete.id)}
                onDelete={(imageId, imageType, imagePath) => handleImageDelete(bookingToComplete.id, imageId, imageType, imagePath)}
                maxImages={15}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCompleteModal(false);
                  setBookingToComplete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleCompleteBooking(true)}
                className="flex items-center gap-2"
              >
                <FiCheck size={16} />
                Skip & Mark Complete
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel Booking Dialog */}
      <Modal
        isOpen={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setBookingToCancel(null);
          setCancelReason('');
        }}
        title="Cancel Booking"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Please provide a reason for cancellation:
          </p>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter cancellation reason..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 resize-none"
            rows="4"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false);
                setBookingToCancel(null);
                setCancelReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCancel}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Confirm Cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </ManagerLayout>
  );
};

export default BookingList;
